// File: src/pages/candidate/CompanyDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import '../../css/HomePage.css';

// --- ICONS ---
const IconMapPin = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const IconUsers = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const IconGlobe = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>;
const IconCheckBadge = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#2563EB" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"></path><path d="M9 11l3 3L18 8"></path></svg>;
const IconBriefcase = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;
const IconPlus = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const IconCheck = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
const DEFAULT_COVER = "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop";

export default function CompanyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const isCandidateRoute = location.pathname.startsWith('/candidate');
    const [currentUserId, setCurrentUserId] = useState(null);

    const Toast = ({ message, onClose }) => {
        useEffect(() => {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }, [onClose]);

        return (
            <div className="toast-notification">
                <span className="material-symbols-outlined" style={{ color: '#4ADE80' }}>check_circle</span>
                {message}
            </div>
        );
    };
    
    const [isFollowed, setIsFollowed] = useState(false);
    const [toastMsg, setToastMsg] = useState(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                if (!id) return;

                const res = await fetch(`${apiBaseUrl}/api/companies/${id}`);
                if (!res.ok) throw new Error("Failed to fetch");

                const token = localStorage.getItem('skillmatch_token');
                if (token && id) {
                    const userRes = await fetch(`${apiBaseUrl}/api/users/me/interactions`, {
                        headers: { 'x-auth-token': token }
                    });
                    if (userRes.ok) {
                        const userData = await userRes.json();
                        if (userData.followedCompanies.includes(id)) {
                            setIsFollowed(true);
                        }
                    }
                }

                const data = await res.json();
                setCompany(data);
            } catch (error) {
                console.error("Lỗi tải công ty:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCompany();
    }, [id, apiBaseUrl]);

    const handleFollow = async () => {
        const token = localStorage.getItem('skillmatch_token');
        if (!token) return navigate('/login');

        try {
            const res = await fetch(`${apiBaseUrl}/api/users/toggle-follow/${id}`, {
                method: 'PUT',
                headers: { 'x-auth-token': token }
            });
            const data = await res.json();

            if (res.ok) {
                setIsFollowed(data.followed);
                setToastMsg(data.message);
                
                if (data.followersCount !== undefined) {
                    setCompany(prev => ({ ...prev, followersCount: data.followersCount }));
                } else {
                    setCompany(prev => ({
                        ...prev,
                        followersCount: data.followed ? ((prev.followersCount || 0) + 1) : ((prev.followersCount || 0) - 1)
                    }));
                }
            }
        } catch (error) {
            console.error("Lỗi follow:", error);
        }
    };

    // ✅ Đã sửa lại URL Google Maps để iframe có thể hoạt động
    const getMapUrl = (address) => {
        const query = encodeURIComponent(address || "Vietnam");
        return `https://maps.google.com/maps?q=${query}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
    };

    if (loading) return (

        <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#F9FAFB' }}>

            <span className="material-symbols-outlined spin" style={{ fontSize: 40, color: '#2563EB' }}>sync</span>

        </div>

    );

    if (!company) return (
        <div className="not-found-screen">
            <h3>Không tìm thấy công ty</h3>
            <button className="btn-back" onClick={() => navigate(-1)}>Quay lại</button>
        </div>
    );
    
    const isOwner = currentUserId && company._id && String(currentUserId) === String(company._id);

    return (
        <div className="company-detail-wrapper">
            {/* 1. COVER IMAGE */}
            <div className="cover-image" style={{ backgroundImage: `url(${company.coverImage || DEFAULT_COVER})` }}>
                <div className="cover-overlay"></div>
            </div>

            {/* 2. HEADER INFO CARD */}
            <div className="header-container">
                <div className="header-card">
                    <div className="logo-wrapper">
                        {company.logo ? (
                            <img src={company.logo} alt="Logo" />
                        ) : (
                            <div className="logo-placeholder">{company.name?.charAt(0)}</div>
                        )}
                    </div>

                    <div className="info-main">
                        <div className="name-row">
                            <h1 className="company-name">{company.name}</h1>
                            <div className="verified-badge" title="Tài khoản doanh nghiệp đã xác thực">
                                <IconCheckBadge />
                                <span>Đã xác thực</span>
                            </div>
                        </div>

                        <div className="meta-row">
                            <span className="meta-item"><IconGlobe /> <a href={company.website || '#'} target="_blank" rel="noreferrer">{company.website || 'Đang cập nhật website'}</a></span>
                            <span className="meta-item"><IconUsers /> {company.size || 'Chưa cập nhật quy mô'}</span>
                            <span className="meta-item"><IconMapPin /> {company.location || company.address || 'Chưa cập nhật'}</span>
                            <span className="meta-item followers-count">
                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span>
                                {company.followersCount || 0} người theo dõi
                            </span>
                        </div>
                    </div>

                    {!isOwner && (
                        <div className="action-wrapper">
                            <button onClick={handleFollow} className={`btn-follow ${isFollowed ? 'following' : ''}`}>
                                {isFollowed ? <><IconCheck /> Đang theo dõi</> : <><IconPlus /> Theo dõi</>}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* 3. MAIN CONTENT GRID */}
            <div className="content-grid">
                {/* LEFT COLUMN: ABOUT & JOBS */}
                <div className="main-col">
                    <div className="content-card">
                        <h2 className="section-title">Giới thiệu công ty</h2>
                        <div className="description-text">
                            {company.description ? (
                                company.description.split('\n').map((line, i) => <p key={i}>{line}</p>)
                            ) : (
                                <p className="text-empty">Nội dung đang được cập nhật...</p>
                            )}
                        </div>
                    </div>

                    <div className="content-card">
                        <h2 className="section-title">Tuyển dụng ({company.jobs ? company.jobs.length : 0})</h2>
                        {company.jobs && company.jobs.length > 0 ? (
                            <div className="jobs-list">
                                {company.jobs.map(job => (
                                    job && (
                                        <div key={job._id} className="job-item">
                                            <div className="job-info">
                                                <h4 className="job-title">{job.title}</h4>
                                                <div className="job-meta">
                                                    <span><span className="material-symbols-outlined">paid</span> {job.salary}</span>
                                                    <span><span className="material-symbols-outlined">schedule</span> {job.type}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate(isCandidateRoute ? `/candidate/jobs/${job._id}` : `/jobs/${job._id}`)}
                                                className="btn-apply"
                                            >
                                                Ứng tuyển
                                            </button>
                                        </div>
                                    )
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <span className="material-symbols-outlined icon">work_off</span>
                                <p>Hiện tại công ty chưa có vị trí nào đang mở.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: SIDEBAR INFO */}
                <div className="sidebar-col">
                    <div className="content-card">
                        <h3 className="sidebar-title">Thông tin liên hệ</h3>
                        <div className="sidebar-info-list">
                            <div className="info-item">
                                <span className="label">Địa chỉ:</span>
                                <span className="val">{company.address || company.location || 'Chưa cập nhật'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Quy mô:</span>
                                <span className="val">{company.size || 'Đang cập nhật'}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Lĩnh vực:</span>
                                <span className="val">{company.industry || 'Đa ngành'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="content-card map-card">
                        {company.address || company.location ? (
                            <iframe
                                title="Company Location"
                                src={getMapUrl(company.address || company.location)}
                                allowFullScreen=""
                                loading="lazy"
                            ></iframe>
                        ) : (
                            <div className="empty-map">Chưa cập nhật địa điểm</div>
                        )}
                    </div>
                </div>
            </div>

            {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}

            {/* CSS INLINE CẬP NHẬT GIAO DIỆN */}
            <style>{`
                .navbar { display: none !important; }
                * { box-sizing: border-box; }
                
                .company-detail-wrapper { 
                    background: #F8FAFC; 
                    min-height: 100vh; 
                    padding-bottom: 60px; 
                    font-family: 'Inter', system-ui, sans-serif;
                }

                /* Utils */
                .loading-screen { min-height: 100vh; display: flex; justify-content: center; align-items: center; background: #F9FAFB; }
                .loading-screen .spin { font-size: 40px; color: #2563EB; animation: spin 1s linear infinite; }
                .not-found-screen { padding: 80px 20px; text-align: center; }
                .btn-back { padding: 10px 20px; background: #2563EB; color: white; border: none; border-radius: 8px; cursor: pointer; margin-top: 16px; font-weight: 500; }
                @keyframes spin { 100% { transform: rotate(360deg); } }

                /* Toast */
                .toast-notification {
                    position: fixed; bottom: 24px; left: 24px; z-index: 10000;
                    background: #1F2937; color: white; padding: 14px 24px; border-radius: 12px;
                    display: flex; align-items: center; gap: 12px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15);
                    font-size: 14px; font-weight: 500;
                    animation: slideUpToast 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes slideUpToast { 0% { transform: translateY(100px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }

                /* Cover Image */
                .cover-image { 
                    height: 260px; width: 100%; 
                    background-size: cover; background-position: center; 
                    position: relative; 
                }
                .cover-overlay {
                    position: absolute; inset: 0;
                    background: linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.3) 100%);
                }

                /* Header Card - Đã sửa lỗi overlap bằng margin-top */
                .header-container {
                    max-width: 1140px; margin: 0 auto; padding: 0 20px;
                    position: relative; z-index: 10;
                    margin-top: -60px; /* Kéo thẻ lên đè vào ảnh bìa thay vì dùng top/absolute */
                    margin-bottom: 24px;
                }
                .header-card {
                    background: white; border-radius: 16px; padding: 28px 32px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    display: flex; gap: 24px; align-items: center;
                }
                
                .logo-wrapper { 
                    width: 120px; height: 120px; flex-shrink: 0;
                    background: white; padding: 4px; border-radius: 16px; 
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08); 
                    border: 1px solid #F1F5F9; overflow: hidden; 
                }
                .logo-wrapper img { width: 100%; height: 100%; object-fit: contain; border-radius: 12px; }
                .logo-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #F1F5F9; font-size: 40px; font-weight: 800; color: #94A3B8; border-radius: 12px; }
                
                .info-main { flex: 1; }
                .name-row { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
                .company-name { font-size: 26px; font-weight: 800; color: #0F172A; margin: 0; line-height: 1.2; }
                
                .verified-badge { 
                    display: inline-flex; align-items: center; gap: 6px; 
                    background: #EFF6FF; color: #1D4ED8; padding: 6px 10px; border-radius: 20px;
                    font-size: 13px; font-weight: 600; border: 1px solid #DBEAFE;
                }

                .meta-row { display: flex; gap: 20px; color: #475569; font-size: 14px; flex-wrap: wrap; }
                .meta-item { display: flex; align-items: center; gap: 6px; }
                .meta-item a { color: #475569; text-decoration: none; transition: color 0.2s; }
                .meta-item a:hover { color: #2563EB; text-decoration: underline; }
                .followers-count { color: #2563EB; font-weight: 600; background: #EFF6FF; padding: 4px 10px; border-radius: 8px; }

                /* Buttons */
                .action-wrapper { flex-shrink: 0; }
                .btn-follow { 
                    display: flex; align-items: center; gap: 8px; padding: 12px 24px; border-radius: 10px;
                    border: none; background: #2563EB; color: white; font-weight: 600; font-size: 15px;
                    cursor: pointer; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2); transition: all 0.2s ease;
                }
                .btn-follow:hover { background: #1D4ED8; transform: translateY(-2px); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3); }
                .btn-follow.following { background: white; color: #334155; border: 1px solid #CBD5E1; box-shadow: none; }
                .btn-follow.following:hover { background: #F8FAFC; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }

                /* Content Grid */
                .content-grid { 
                    max-width: 1140px; margin: 0 auto; padding: 0 20px;
                    display: grid; grid-template-columns: 2fr 1fr; gap: 24px;
                }

                .content-card { 
                    background: white; border-radius: 16px; padding: 28px; 
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 24px; border: 1px solid #F1F5F9;
                }
                .section-title { font-size: 18px; font-weight: 700; color: #0F172A; margin-top: 0; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; }
                
                .description-text p { color: #475569; line-height: 1.7; margin-bottom: 14px; font-size: 15px; }
                .text-empty { color: #94A3B8; font-style: italic; }

                /* Jobs List */
                .jobs-list { display: flex; flex-direction: column; gap: 16px; }
                .job-item { 
                    display: flex; justify-content: space-between; align-items: center; 
                    padding: 20px; border: 1px solid #E2E8F0; border-radius: 12px; 
                    transition: all 0.2s; flex-wrap: wrap; gap: 16px; background: #FAFAFA;
                }
                .job-item:hover { border-color: #93C5FD; background: white; box-shadow: 0 4px 12px rgba(0,0,0,0.03); transform: translateY(-2px); }
                
                .job-title { margin: 0 0 8px 0; font-size: 16px; font-weight: 700; color: #0F172A; }
                .job-meta { display: flex; gap: 16px; font-size: 13px; color: #64748B; }
                .job-meta span { display: flex; align-items: center; gap: 4px; font-weight: 500; }
                .job-meta .material-symbols-outlined { font-size: 18px; color: #94A3B8; }
                
                .btn-apply { 
                    padding: 8px 20px; background: white; border: 1.5px solid #2563EB; 
                    color: #2563EB; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s;
                }
                .job-item:hover .btn-apply { background: #2563EB; color: white; }

                .empty-state { text-align: center; padding: 40px 20px; color: #94A3B8; }
                .empty-state .icon { font-size: 48px; margin-bottom: 12px; display: block; opacity: 0.5; }

                /* Sidebar */
                .sidebar-title { font-size: 16px; font-weight: 700; color: #0F172A; margin-top: 0; margin-bottom: 20px; }
                .sidebar-info-list { display: flex; flex-direction: column; gap: 16px; }
                .info-item { display: flex; flex-direction: column; gap: 4px; }
                .info-item .label { font-size: 13px; color: #64748B; font-weight: 500; }
                .info-item .val { font-size: 14.5px; color: #0F172A; font-weight: 500; line-height: 1.4; }

                /* Map Card */
                .map-card { padding: 0; overflow: hidden; height: 260px; border-radius: 16px; }
                .map-card iframe { width: 100%; height: 100%; border: none; }
                .empty-map { display: flex; align-items: center; justify-content: center; height: 100%; color: #94A3B8; background: #F1F5F9; font-size: 14px; font-weight: 500; }

                /* Responsive */
                @media (max-width: 900px) {
                    .header-card { flex-direction: column; align-items: center; text-align: center; padding: 24px; gap: 16px; }
                    .logo-wrapper { margin-top: -60px; /* Nhô logo lên trên xíu ở mobile */ }
                    .name-row { justify-content: center; }
                    .meta-row { justify-content: center; }
                    .content-grid { grid-template-columns: 1fr; }
                    .header-container { margin-top: -40px; }
                }
                @media (max-width: 500px) {
                    .job-item { flex-direction: column; align-items: flex-start; }
                    .btn-apply { width: 100%; }
                }
            `}</style>
        </div>
    );
}